import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MainState } from "@app/store/state";
import { CommonApiService } from "@core/services/api/classic/common/common-api.service";
import { AuthService } from "@core/services/auth.service";
import { LoadingService } from "@core/services/loading.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { WindowRefService } from "@core/services/window-ref.service";
import {
  Auth2FAActionTypes,
  Login2FARequired,
  Session2FAExpired,
  Verify2FA,
  Verify2FAFailure,
  Verify2FASuccess
} from "@features/account/store/auth-2fa.actions";
import { LoginSuccess } from "@features/account/store/auth.actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie";
import { of, forkJoin } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";

@Injectable()
export class Auth2FAEffects {
  Login2FARequired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Auth2FAActionTypes.LOGIN_2FA_REQUIRED),
        tap((action: Login2FARequired) => {
          this.loadingService.setLoading(false);
          const redirectUrl = this.authService.getPendingRedirectUrl();
          const queryParams: any = { method: action.payload.twoFactorMethod };
          if (redirectUrl) {
            queryParams.redirectUrl = redirectUrl;
          }
          void this.router.navigate(["account", "verify-2fa"], { queryParams });
        })
      ),
    { dispatch: false }
  );

  Verify2FA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Auth2FAActionTypes.VERIFY_2FA),
      tap(() => this.loadingService.setLoading(true)),
      switchMap((action: Verify2FA) =>
        this.authService.verify2FA(action.payload.otpCode).pipe(
          tap(response => {
            const url = this.windowRefService.getCurrentUrl().toString();
            const options: any = {
              path: "/",
              expires: new Date(new Date().getTime() + 180 * 24 * 60 * 60 * 1000)
            };

            // Add domain based on current URL
            if (url?.includes("astrobin.com")) {
              options.domain = ".astrobin.com";
            } else if (url?.includes("localhost")) {
              options.domain = "localhost";
            }

            this.cookieService.put(AuthService.CLASSIC_AUTH_TOKEN_COOKIE, response.token, options);
          }),
          switchMap(response =>
            forkJoin({
              user: this.commonApiService.getCurrentUser(),
              userProfile: this.commonApiService.getCurrentUserProfile(),
              userSubscriptions: this.commonApiService.getCurrentUserSubscriptions()
            }).pipe(
              map(
                data =>
                  new LoginSuccess({
                    user: data.user,
                    userProfile: data.userProfile,
                    userSubscriptions: data.userSubscriptions,
                    redirectUrl: this.authService.getPendingRedirectUrl()
                  })
              )
            )
          ),
          catchError(error => {
            this.loadingService.setLoading(false);

            if (error.error?.error === "Invalid or expired session token") {
              // Session expired is still shown as a pop notification since it triggers navigation
              this.popNotificationsService.warning(
                this.translateService.instant("Your session has expired. Please log in again.")
              );
              return of(new Session2FAExpired());
            }

            // Don't show pop notification for OTP errors - they're shown inline in the form
            return of(new Verify2FAFailure({ error }));
          })
        )
      )
    )
  );

  Verify2FASuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Auth2FAActionTypes.VERIFY_2FA_SUCCESS),
        tap((action: Verify2FASuccess) => {
          this.loadingService.setLoading(false);
          void this.router.navigate(["account", "logged-in"], {
            queryParams: { redirectUrl: action.payload.redirectUrl }
          });
        })
      ),
    { dispatch: false }
  );

  Session2FAExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Auth2FAActionTypes.SESSION_EXPIRED),
        tap(() => {
          this.authService.clear2FASession();
          void this.router.navigate(["account", "login"]);
        })
      ),
    { dispatch: false }
  );

  Clear2FASession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Auth2FAActionTypes.CLEAR_2FA_SESSION),
        tap(() => {
          this.authService.clear2FASession();
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<MainState>,
    private readonly authService: AuthService,
    private readonly commonApiService: CommonApiService,
    private readonly cookieService: CookieService,
    private readonly router: Router,
    private readonly loadingService: LoadingService,
    private readonly popNotificationsService: PopNotificationsService,
    private readonly translateService: TranslateService,
    private readonly windowRefService: WindowRefService
  ) {}
}
